import { Dialog, Divider, Stack, Typography } from '@mui/material';
import { PropsWithChildren } from 'react';
import useResponsive from '../../hooks/useResponsive';
import { StyledCardContent } from '../containers/StyledCardContent';
import { CloseIconButton } from '../dialog/CloseIconButton';
import { CenteredRow } from '../containers/CenteredRow';
import Iconify from '../iconify';

export interface FullscreenModalProps extends PropsWithChildren {
  titleText?: string | null;
  subtitleText?: string | null;
  boldSubtitle?: boolean;
  showDialog: boolean;
  onClose: VoidFunction;
  icon?: string;
  id?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullscreenBreakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullscreen?: boolean;
  /** Stretch modal body so embedded content can fill remaining height. */
  fillHeight?: boolean;
}

export function FullscreenModal({
  children,
  titleText = null,
  subtitleText = null,
  boldSubtitle = false,
  showDialog,
  onClose,
  icon,
  id,
  maxWidth = 'sm',
  fullscreenBreakpoint = 'sm',
  fullscreen,
  fillHeight = false,
}: FullscreenModalProps) {
  const fullscreenMode = useResponsive('down', fullscreenBreakpoint);
  const isFullScreen = fullscreen || fullscreenMode;

  return (
    <Dialog
      fullScreen={isFullScreen}
      open={showDialog}
      maxWidth={maxWidth}
      fullWidth
      onClose={onClose}
      id={id}
      PaperProps={
        fillHeight
          ? {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '100dvh',
                height: isFullScreen ? '100%' : '90dvh',
              },
            }
          : undefined
      }
    >
      <CloseIconButton onClose={onClose} />
      <StyledCardContent
        sx={
          fillHeight
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <Stack
          spacing={1}
          sx={
            fillHeight
              ? {
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                }
              : undefined
          }
        >
          <CenteredRow>
            {icon && <Iconify icon={icon} width={24} mr={0.5} />}
            <Typography variant="h6">{titleText || ''}</Typography>
          </CenteredRow>
          {subtitleText && (
            <>
              <Divider />
              <CenteredRow>
                <Typography variant={boldSubtitle ? 'h6' : 'body1'}>{subtitleText}</Typography>
              </CenteredRow>
            </>
          )}
          {children}
        </Stack>
      </StyledCardContent>
    </Dialog>
  );
}
